import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ArrowUpRight, ChevronDown, Fuel, TrendingUp, HardHat } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1667667708185-e80206929c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHdpbmQlMjBmYXJtJTIwY2luZW1hdGljJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3MTQ4NTkzMHww&ixlib=rb-4.1.0&q=85";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/c5vdggqv_Group%20194.png";

const DIESELS_LOGO = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/v3ubrgjb_Vector.png";

const INNOVEST_LOGO = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/m17x1mzm_Vector-1.png";

const SUBSIDIARIES = [
  {
    icon: Fuel,
    logo: DIESELS_LOGO,
    name: "Sharda Diesels",
    tagline: "Power Systems Manufacturing",
    website: "https://shardadiesels.in",
    desc: "Sharda Diesels is heavy-duty power, engineered to last — delivering industrial diesel generator solutions for construction, mining, telecom, and standby applications. Decades of expertise in rotating machinery, combined with modern emission standards, keep India's infrastructure running without compromise.",
    capabilities: ["DG Sets (5kVA - 2500kVA)", "Power Systems Integration", "AMF & Synchronization Panels", "Annual Maintenance Contracts"],
  },
  {
    icon: TrendingUp,
    logo: INNOVEST_LOGO,
    name: "Sharda Innovest",
    tagline: "Strategic Investments & Ventures",
    website: "https://shardainnovest.com",
    desc: "Sharda Innovest is where deep industry knowledge meets strategic capital. The group's investment and venture arm, it backs high-growth opportunities across clean energy, advanced manufacturing, and technology — building long-term value with the discipline and foresight of a seasoned industrial house.",
    capabilities: ["Cleantech Investments", "Strategic Partnerships", "Portfolio Management", "Market Development"],
  },
  {
    icon: HardHat,
    name: "Sharda Infra",
    tagline: "Infrastructure & Industrial Development",
    website: "https://prismoline.com",
    desc: "Sharda Infra builds the backbone India's industrial growth runs on — land development, industrial parks, facility construction, and turnkey project execution, delivered with the precision and scale that serious manufacturing and logistics demand.",
    capabilities: ["Industrial Parks", "Facility Construction", "Land Development", "Turnkey Project Execution"],
  },
];

export default function ShardaGroupPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <main data-testid="sharda-group-page" className="pt-24 md:pt-[116px]">
      {/* Hero */}
      <section ref={heroRef} className="relative pt-8 md:pt-10 pb-20 md:pb-24 lg:pb-28 bg-[#040D1C] noise-overlay overflow-hidden">
        {/* Background image with parallax */}
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0 will-change-transform">
          <div className="ken-burns w-full h-full">
            <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#040D1C] via-[#040D1C]/70 to-[#040D1C]/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#040D1C] via-transparent to-transparent" />
        </motion.div>
        <div className="absolute inset-0 hero-glow pointer-events-none z-[1]" />
        <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 will-change-transform">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Link to="/" data-testid="group-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#1E96FC] transition-colors uppercase tracking-wider mb-6 md:mb-8 min-h-[44px]">
              <ArrowLeft className="w-4 h-4" />
              Back to Sharda Energy Tech
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 rounded-sm bg-[#1E96FC]/10 border border-[#1E96FC]/20 text-white text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] mb-6 md:mb-8">
              The Sharda Group
            </span>

            <h1 className="font-display font-bold uppercase leading-tight tracking-tight text-slate-100 stroke-on-hover cursor-default">
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#1E96FC]">A legacy of</span>
              <span className="block text-white text-[clamp(2.5rem,9.5vw,8.4rem)] leading-[0.85] stroke-fill-white">Industrial excellence</span>
            </h1>

            <p className="text-sm md:text-base lg:text-lg text-slate-400 max-w-2xl leading-relaxed mt-10 md:mt-14">
              <span className="text-[#1E96FC]">The Sharda Group</span> is decades of
              industrial expertise, deep-rooted across power generation, energy systems,
              infrastructure, and strategic investments — built on real engineering, real
              relationships, and a legacy that speaks for itself.
            </p>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-12 md:mt-16 flex flex-col items-center gap-2"
          >
            <span className="text-[10px] text-slate-500 uppercase tracking-[0.3em]">Scroll</span>
            <ChevronDown className="w-4 h-4 text-slate-500 scroll-indicator" />
          </motion.div>
        </motion.div>
      </section>

      {/* Subsidiaries */}
      <section className="py-16 md:py-24 lg:py-32 noise-overlay">
        <div className="section-glow absolute inset-0 pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-4">
            <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
              // Group Companies
            </span>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-10 md:mb-16">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight">
              <span className="block">Divisions driving</span>
              <span className="block text-[#1E96FC]">India's industrial growth</span>
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={staggerContainer}
            className="space-y-5 md:space-y-8"
          >
            {SUBSIDIARIES.map((sub, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                data-testid={`group-company-${i}`}
                className="glass-card rounded-sm p-6 md:p-8 lg:p-10 group hover:bg-[#003771]/20 transition-all duration-300"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                  <div className="md:col-span-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center transition-colors overflow-hidden ${sub.logo ? "bg-white group-hover:bg-white/90" : "bg-[#1E96FC]/10 group-hover:bg-[#1E96FC]/20"}`}>
                        {sub.logo ? (
                          <img src={sub.logo} alt={sub.name} className="w-7 h-7 object-contain" />
                        ) : (
                          <sub.icon className="w-5 h-5 text-[#1E96FC]" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl lg:text-2xl font-bold uppercase text-slate-100 tracking-wide">
                          {sub.name}
                        </h3>
                        <span className="text-xs text-[#1E96FC] uppercase tracking-widest">
                          {sub.tagline}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed max-w-2xl">
                      {sub.desc}
                    </p>
                    {sub.website && (
                      <a
                        href={sub.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`group-company-website-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="inline-flex items-center gap-2 mt-5 md:mt-6 px-5 py-2.5 border border-slate-700 text-slate-300 bg-transparent hover:bg-[#1E96FC] hover:border-[#1E96FC] hover:text-white uppercase tracking-widest text-[10px] lg:text-[11px] rounded-sm font-bold transition-colors"
                      >
                        Visit Website
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  <div className="md:col-span-4">
                    <h5 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
                      Key Capabilities
                    </h5>
                    <div className="flex flex-col gap-2">
                      {sub.capabilities.map((cap) => (
                        <span
                          key={cap}
                          className="px-3 py-1.5 bg-[#003771]/30 text-xs text-slate-300 uppercase tracking-wider rounded-sm border border-[#003771]/50"
                        >
                          {cap}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <Separator className="my-10 md:my-16 bg-[#003771]/30" />

          {/* CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={fadeInUp}
            className="text-center"
          >
            <h3 className="font-display text-xl md:text-2xl lg:text-3xl font-bold uppercase text-slate-200 mb-3 md:mb-4">
              Interested in Sharda Energy Tech?
            </h3>
            <p className="text-sm text-slate-400 mb-6 md:mb-8 max-w-lg mx-auto">
              Explore our advanced battery manufacturing and energy storage solutions.
            </p>
            <Link to="/" data-testid="group-explore-energy-link">
              <Button className="bg-[#1E96FC] text-white hover:bg-[#1E96FC]/90 rounded-sm px-6 md:px-8 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200 hover:scale-[1.02] min-h-[48px]">
                Explore Energy Solutions
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
